import { Middleware } from "@reduxjs/toolkit";
import { RootState } from "../index";
import { setStaterooms, setStateroomConfig, setPowerPlant, setFuelTanks } from "../features/shipBuilderSlice";
import { setSectionStateroomConfig } from "../features/breakawayHullSlice";
import { calculatePowerPlantRequirements } from "../../utils/calculations/powerCalculations";
import { calculateFuelTankRequirements } from "../../utils/calculations/fuelCalculations";

const stateroomPowerPlantMiddleware: Middleware<{}, RootState> =
  (store) => (next) => (action: any) => {
    const result = next(action);

    // Process stateroom configuration changes
    if (setStaterooms.match(action) || setStateroomConfig.match(action) || setSectionStateroomConfig.match(action)) {
      const state = store.getState().shipBuilder;
      const breakawayState = store.getState().breakawayHull;
      const { stateroomConfig, shipSize, techLevel, drives, powerPlant } = state;

      // Calculate stateroom power requirements
      let stateroomPowerRequirement = 0;

      // Add main ship stateroom power
      if (stateroomConfig) {
        // Single staterooms: 1 power each
        stateroomPowerRequirement += (stateroomConfig.single || 0) * 1;
        
        // Double staterooms: 1 power each  
        stateroomPowerRequirement += (stateroomConfig.double || 0) * 1;
        
        // Low berths: 1 power per 20 berths (round up)
        const lowBerthsPower = Math.ceil((stateroomConfig.lowBerths?.count || 0) / 20);
        stateroomPowerRequirement += lowBerthsPower;
        
        // Emergency low berths: 1 power per 20 berths (round up)
        const emergencyLowBerthsPower = Math.ceil((stateroomConfig.emergencyLowBerths?.count || 0) / 20);
        stateroomPowerRequirement += emergencyLowBerthsPower;
      }

      // Add breakaway hull stateroom power if breakaway hulls are active
      if (breakawayState?.isBreakaway && breakawayState.sections) {
        breakawayState.sections.forEach(section => {
          if (section.stateroomConfig) {
            // Single staterooms: 1 power each
            stateroomPowerRequirement += (section.stateroomConfig.singleStaterooms || 0) * 1;
            
            // Double staterooms: 1 power each
            stateroomPowerRequirement += (section.stateroomConfig.doubleStaterooms || 0) * 1;
            
            // Low berths: 1 power per 20 berths (round up)
            const lowBerthsPower = Math.ceil((section.stateroomConfig.lowBerths || 0) / 20);
            stateroomPowerRequirement += lowBerthsPower;
            
            // Emergency low berths: 1 power per 20 berths (round up)
            const emergencyLowBerthsPower = Math.ceil((section.stateroomConfig.emergencyLowBerths || 0) / 20);
            stateroomPowerRequirement += emergencyLowBerthsPower;
          }
        });
      }

      // Check if current power plant can handle the new power requirements
      const currentPowerOutput = powerPlant ? powerPlant.power : 0;
      const basePowerRequired = state.shipStats?.powerRequired || (shipSize * 0.2);
      const totalPowerRequired = basePowerRequired + stateroomPowerRequirement;

      if (currentPowerOutput < totalPowerRequired) {
        // Calculate new power plant requirements
        const powerPlantResult = calculatePowerPlantRequirements({
          powerRequired: totalPowerRequired,
          shipSize,
          techLevel,
        });

        if (powerPlantResult.success && powerPlantResult.data) {
          // Update power plant
          store.dispatch(setPowerPlant(powerPlantResult.data));

          // Update fuel tanks based on new power plant
          const fuelTankResult = calculateFuelTankRequirements({
            powerPlant: powerPlantResult.data,
            drives,
            shipSize,
          });

          if (fuelTankResult.success && fuelTankResult.data) {
            store.dispatch(setFuelTanks(fuelTankResult.data.total));
          }
        }
      }
    }

    return result;
  };

export default stateroomPowerPlantMiddleware;
